import { useSearchParams } from 'react-router-dom';
import { useAuthStore, useDrawerStore } from '../../../store';
import { Spin } from 'antd';
import BaseTable from '../../../components/BaseTable';
import InfoModal from '../../../components/ui/modal/InfoModal';
import React, { useEffect, useState } from 'react';
import { useDeleteData, useFetchData, useLocationStore } from '../../../hooks';
import { LocationModel, PageableResponse } from '../../../types';
import { Resource } from '../../../lib/enums';
import { convertDate, dateTimeFormat } from '../../../lib/utils';

const LocationTable = ({ api }: { api: string }) => {
  const [searchParams] = useSearchParams();
  const drawerStore = useDrawerStore();
  const locationStore = useLocationStore();
  const [spinning, setSpinning] = useState<boolean>(false);
  const [isInfoModalOpen, setInfoModalOpen] = useState(false);
  const { user } = useAuthStore();
  const userModuleAccess = user?.moduleAccess?.find(
    (item) => item.module.code === 'LOC',
  );

  const { data, isLoading } = useFetchData<PageableResponse<LocationModel>>(
    api,
    {
      resource: searchParams.get('resource') || Resource.FULL,
      search: searchParams.get('search') || '',
      length: parseInt(searchParams.get('length') || '999999'),
    },
  );

  const {
    mutate,
    isPending: isPendingDelete,
    isSuccess: isSuccessDelete,
  } = useDeleteData(api);

  const columns = [
    {
      title: 'Code',
      dataIndex: 'code',
    },
    {
      title: 'Description',
      dataIndex: 'description',
    },
    {
      title: 'Warehouse',
      dataIndex: 'warehouse',
      render: (_text: string, record: LocationModel) =>
        `${record?.warehouse?.description}`,
    },
    {
      title: 'Created By/Date',
      dataIndex: 'createdBy',
      render: (_text: string, record: any) => {
        return (
          <div className="flex flex-col">
            <span className="mr-2">{record.createdBy}</span>
            {record.timeCreated != null && (
              <span>{convertDate(record.timeCreated, dateTimeFormat)}</span>
            )}
          </div>
        );
      },
    },
    {
      title: 'Update By/Date',
      dataIndex: 'updatedBy',
      render: (_text: string, record: any) => {
        return (
          <div className="flex flex-col">
            <span className="mr-2">{record.updatedBy}</span>
            {record.timeUpdated != null && (
              <span>{convertDate(record.timeUpdated, dateTimeFormat)}</span>
            )}
          </div>
        );
      },
    },
  ];

  const handleDataDelete = (data: LocationModel) => {
    mutate({ id: data.id });
    setSpinning(true);
  };

  const handleDataEdit = (data: LocationModel) => {
    locationStore.setData(data);
    drawerStore.onClose();
  };

  useEffect(() => {
    if (isSuccessDelete) setSpinning(false);
  }, [isSuccessDelete]);

  const handleInfoModalClose = () => {
    setInfoModalOpen(false);
  };

  return (
    <>
      <Spin spinning={spinning} fullscreen />
      <BaseTable
        dataSource={data?.data || []}
        isLoading={isLoading || isPendingDelete}
        handleDeleteButtonClick={handleDataDelete}
        handleEditButtonClick={handleDataEdit}
        columns={columns}
        canDelete={userModuleAccess?.delete}
        canEdit={userModuleAccess?.write}
        userRole={user?.role}
        module={'Warehouse'}
      />
      <InfoModal
        visible={isInfoModalOpen}
        message={'Warehouse successfully deleted'}
        onClose={handleInfoModalClose}
      />
    </>
  );
};

export default LocationTable;
